/**
 * Admin Firestore Service
 * Firebase Firestore ile admin panel verilerini yönetir
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseFirestore, isFirebaseConfigured } from './config';

// Collection names
export const COLLECTIONS = {
  ADMIN_USERS: 'admin_users',
  WEBSITE_USERS: 'website_users',
  EXTENSIONS: 'extensions',
  CONTENT: 'content',
  ANALYTICS: 'analytics',
  PAGE_VIEWS: 'page_views',
  DOWNLOADS: 'downloads',
  DAILY_STATS: 'daily_stats',
  AI_PROVIDERS: 'ai_providers',
  AI_MODELS: 'ai_models',
  PRICING: 'pricing',
  SETTINGS: 'settings',
  REVENUE: 'revenue',
  AI_USAGE: 'ai_usage',
  SUBSCRIPTIONS: 'subscriptions',
} as const;

// ============================================================================
// USERS
// ============================================================================

export interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'moderator' | 'user';
  status: 'active' | 'inactive' | 'suspended';
  plan: 'free' | 'pro' | 'enterprise';
  avatar?: string;
  createdAt: Date;
  lastLoginAt?: Date;
  downloadsCount: number;
  extensionsCount: number;
}

export async function getUsers(): Promise<FirestoreUser[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const usersRef = collection(db, COLLECTIONS.WEBSITE_USERS);
  const q = query(usersRef, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      lastLoginAt: (data.lastLoginAt as Timestamp)?.toDate(),
    } as FirestoreUser;
  });
}

export async function addUser(user: Omit<FirestoreUser, 'id' | 'createdAt'>): Promise<string> {
  if (!isFirebaseConfigured()) throw new Error('Firebase not configured');

  const db = getFirebaseFirestore();
  const usersRef = collection(db, COLLECTIONS.WEBSITE_USERS);
  const newRef = doc(usersRef);

  await setDoc(newRef, {
    ...user,
    createdAt: serverTimestamp(),
  });

  return newRef.id;
}

export async function updateUser(userId: string, updates: Partial<FirestoreUser>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const userRef = doc(db, COLLECTIONS.WEBSITE_USERS, userId);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteUser(userId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const userRef = doc(db, COLLECTIONS.WEBSITE_USERS, userId);
  await deleteDoc(userRef);
}

export function onUsersChange(callback: (users: FirestoreUser[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const usersRef = collection(db, COLLECTIONS.WEBSITE_USERS);
  const q = query(usersRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const users = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          lastLoginAt: (data.lastLoginAt as Timestamp)?.toDate(),
        } as FirestoreUser;
      });
      callback(users);
    },
    (error) => {
      console.debug('Users listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// EXTENSIONS
// ============================================================================

export interface FirestoreExtension {
  id: string;
  name: string;
  publisher: string;
  publisherEmail?: string;
  version: string;
  description: string;
  longDescription?: string;
  status: 'approved' | 'pending' | 'rejected';
  downloads: number;
  rating: number;
  ratingCount: number;
  submittedAt: Date;
  reviewedAt?: Date;
  reviewedBy?: string;
  category: string;
  tags: string[];
  icon?: string;
  repositoryUrl?: string;
}

export async function getExtensions(): Promise<FirestoreExtension[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const extRef = collection(db, COLLECTIONS.EXTENSIONS);
  const q = query(extRef, orderBy('submittedAt', 'desc'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      submittedAt: (data.submittedAt as Timestamp)?.toDate() || new Date(),
      reviewedAt: (data.reviewedAt as Timestamp)?.toDate(),
    } as FirestoreExtension;
  });
}

export async function addExtension(ext: Omit<FirestoreExtension, 'id' | 'submittedAt' | 'downloads' | 'rating' | 'ratingCount'>): Promise<string> {
  if (!isFirebaseConfigured()) throw new Error('Firebase not configured');

  const db = getFirebaseFirestore();
  const extRef = collection(db, COLLECTIONS.EXTENSIONS);
  const newRef = doc(extRef);

  await setDoc(newRef, {
    ...ext,
    downloads: 0,
    rating: 0,
    ratingCount: 0,
    submittedAt: serverTimestamp(),
  });

  return newRef.id;
}

export async function updateExtension(extId: string, updates: Partial<FirestoreExtension>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const extRef = doc(db, COLLECTIONS.EXTENSIONS, extId);
  await updateDoc(extRef, updates);
}

export async function approveExtension(extId: string, reviewedBy: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const extRef = doc(db, COLLECTIONS.EXTENSIONS, extId);
  await updateDoc(extRef, {
    status: 'approved',
    reviewedAt: serverTimestamp(),
    reviewedBy,
  });
}

export async function rejectExtension(extId: string, reviewedBy: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const extRef = doc(db, COLLECTIONS.EXTENSIONS, extId);
  await updateDoc(extRef, {
    status: 'rejected',
    reviewedAt: serverTimestamp(),
    reviewedBy,
  });
}

export async function deleteExtension(extId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const extRef = doc(db, COLLECTIONS.EXTENSIONS, extId);
  await deleteDoc(extRef);
}

export async function incrementExtensionDownloads(extId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const extRef = doc(db, COLLECTIONS.EXTENSIONS, extId);
  await updateDoc(extRef, {
    downloads: increment(1),
  });
}

export function onExtensionsChange(callback: (extensions: FirestoreExtension[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const extRef = collection(db, COLLECTIONS.EXTENSIONS);
  const q = query(extRef, orderBy('submittedAt', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const extensions = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          submittedAt: (data.submittedAt as Timestamp)?.toDate() || new Date(),
          reviewedAt: (data.reviewedAt as Timestamp)?.toDate(),
        } as FirestoreExtension;
      });
      callback(extensions);
    },
    (error) => {
      console.debug('Extensions listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// CONTENT
// ============================================================================

export interface FirestoreContent {
  id: string;
  title: string;
  slug: string;
  type: 'blog' | 'docs' | 'changelog';
  status: 'published' | 'draft' | 'archived';
  author: string;
  authorId: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  views: number;
  tags: string[];
}

export async function getContent(): Promise<FirestoreContent[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const contentRef = collection(db, COLLECTIONS.CONTENT);
  const q = query(contentRef, orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      publishedAt: (data.publishedAt as Timestamp)?.toDate(),
    } as FirestoreContent;
  });
}

export async function addContent(content: Omit<FirestoreContent, 'id' | 'createdAt' | 'updatedAt' | 'views'>): Promise<string> {
  if (!isFirebaseConfigured()) throw new Error('Firebase not configured');

  const db = getFirebaseFirestore();
  const contentRef = collection(db, COLLECTIONS.CONTENT);
  const newRef = doc(contentRef);

  await setDoc(newRef, {
    ...content,
    views: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return newRef.id;
}

export async function updateContent(contentId: string, updates: Partial<FirestoreContent>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const contentRef = doc(db, COLLECTIONS.CONTENT, contentId);
  await updateDoc(contentRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function publishContent(contentId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const contentRef = doc(db, COLLECTIONS.CONTENT, contentId);
  await updateDoc(contentRef, {
    status: 'published',
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function unpublishContent(contentId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const contentRef = doc(db, COLLECTIONS.CONTENT, contentId);
  await updateDoc(contentRef, {
    status: 'draft',
    publishedAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteContent(contentId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const contentRef = doc(db, COLLECTIONS.CONTENT, contentId);
  await deleteDoc(contentRef);
}

export async function incrementContentViews(contentId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const contentRef = doc(db, COLLECTIONS.CONTENT, contentId);
  await updateDoc(contentRef, {
    views: increment(1),
  });
}

export function onContentChange(callback: (content: FirestoreContent[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const contentRef = collection(db, COLLECTIONS.CONTENT);
  const q = query(contentRef, orderBy('updatedAt', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const content = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
          publishedAt: (data.publishedAt as Timestamp)?.toDate(),
        } as FirestoreContent;
      });
      callback(content);
    },
    (error) => {
      console.debug('Content listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// ANALYTICS
// ============================================================================

export interface FirestorePageView {
  id: string;
  path: string;
  timestamp: Date;
  sessionId: string;
  userId?: string;
  referrer?: string;
  userAgent?: string;
  country?: string;
  device: 'desktop' | 'mobile' | 'tablet';
}

export interface FirestoreDownload {
  id: string;
  timestamp: Date;
  platform: 'windows' | 'macos' | 'linux';
  version: string;
  sessionId: string;
  userId?: string;
}

export interface FirestoreDailyStats {
  date: string;
  pageViews: number;
  uniqueVisitors: number;
  downloads: number;
  avgSessionDuration: number;
}

export async function trackPageView(pageView: Omit<FirestorePageView, 'id' | 'timestamp'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const pageViewsRef = collection(db, COLLECTIONS.PAGE_VIEWS);
  const newRef = doc(pageViewsRef);

  await setDoc(newRef, {
    ...pageView,
    timestamp: serverTimestamp(),
  });

  // Also update daily stats
  await updateDailyStats('pageViews');
}

export async function trackDownload(download: Omit<FirestoreDownload, 'id' | 'timestamp'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const downloadsRef = collection(db, COLLECTIONS.DOWNLOADS);
  const newRef = doc(downloadsRef);

  await setDoc(newRef, {
    ...download,
    timestamp: serverTimestamp(),
  });

  // Also update daily stats
  await updateDailyStats('downloads');
}

async function updateDailyStats(field: 'pageViews' | 'downloads'): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const today = new Date().toISOString().split('T')[0];
  const db = getFirebaseFirestore();
  const statsRef = doc(db, COLLECTIONS.DAILY_STATS, today);

  const snap = await getDoc(statsRef);
  if (snap.exists()) {
    await updateDoc(statsRef, {
      [field]: increment(1),
    });
  } else {
    await setDoc(statsRef, {
      date: today,
      pageViews: field === 'pageViews' ? 1 : 0,
      downloads: field === 'downloads' ? 1 : 0,
      uniqueVisitors: 0,
      avgSessionDuration: 0,
    });
  }
}

export async function getDailyStats(days: number = 30): Promise<FirestoreDailyStats[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const statsRef = collection(db, COLLECTIONS.DAILY_STATS);
  const q = query(statsRef, orderBy('date', 'desc'), limit(days));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => doc.data() as FirestoreDailyStats).reverse();
}

export async function getPageViews(days: number = 7): Promise<FirestorePageView[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const pageViewsRef = collection(db, COLLECTIONS.PAGE_VIEWS);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const q = query(
    pageViewsRef,
    where('timestamp', '>=', Timestamp.fromDate(startDate)),
    orderBy('timestamp', 'desc'),
    limit(1000)
  );
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(),
    } as FirestorePageView;
  });
}

export async function getDownloads(days: number = 30): Promise<FirestoreDownload[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const downloadsRef = collection(db, COLLECTIONS.DOWNLOADS);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const q = query(
    downloadsRef,
    where('timestamp', '>=', Timestamp.fromDate(startDate)),
    orderBy('timestamp', 'desc'),
    limit(500)
  );
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(),
    } as FirestoreDownload;
  });
}

// ============================================================================
// SEED DATA (for initial setup)
// ============================================================================

export async function seedInitialData(): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const batch = writeBatch(db);

  // Check if already seeded
  const usersSnap = await getDocs(collection(db, COLLECTIONS.WEBSITE_USERS));
  if (!usersSnap.empty) {
    console.log('Data already seeded');
    return;
  }

  // Seed users
  const users = [
    { name: 'John Doe', email: 'john@example.com', role: 'admin', status: 'active', plan: 'enterprise', downloadsCount: 0, extensionsCount: 0 },
    { name: 'Jane Smith', email: 'jane@example.com', role: 'moderator', status: 'active', plan: 'pro', downloadsCount: 0, extensionsCount: 0 },
    { name: 'Bob Wilson', email: 'bob@example.com', role: 'user', status: 'active', plan: 'free', downloadsCount: 0, extensionsCount: 0 },
  ];

  users.forEach((user) => {
    const ref = doc(collection(db, COLLECTIONS.WEBSITE_USERS));
    batch.set(ref, { ...user, createdAt: serverTimestamp() });
  });

  // Seed extensions
  const extensions = [
    { name: 'Python Language Support', publisher: 'Ochtopus', version: '2.1.0', description: 'Rich support for Python', status: 'approved', category: 'Languages', tags: ['python', 'language'], downloads: 0, rating: 0, ratingCount: 0 },
    { name: 'Dark Pro Theme', publisher: 'ThemeStudio', version: '1.5.2', description: 'A professional dark theme', status: 'approved', category: 'Themes', tags: ['theme', 'dark'], downloads: 0, rating: 0, ratingCount: 0 },
  ];

  extensions.forEach((ext) => {
    const ref = doc(collection(db, COLLECTIONS.EXTENSIONS));
    batch.set(ref, { ...ext, submittedAt: serverTimestamp() });
  });

  // Seed content
  const content = [
    { title: 'Welcome to Ochtopus', slug: 'welcome-to-ochtopus', type: 'blog', status: 'published', author: 'Admin', authorId: 'admin', content: '# Welcome', excerpt: 'Welcome post', tags: ['announcement'], views: 0 },
    { title: 'Getting Started Guide', slug: 'getting-started', type: 'docs', status: 'published', author: 'Admin', authorId: 'admin', content: '# Getting Started', excerpt: 'Learn how to get started', tags: ['tutorial'], views: 0 },
  ];

  content.forEach((c) => {
    const ref = doc(collection(db, COLLECTIONS.CONTENT));
    batch.set(ref, { ...c, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), publishedAt: serverTimestamp() });
  });

  await batch.commit();
  console.log('Initial data seeded successfully');
}

// ============================================================================
// AI PROVIDERS & MODELS
// ============================================================================

export type AIProviderType = 'openai' | 'anthropic' | 'google' | 'xai' | 'ollama' | 'mistral' | 'cohere';

export interface FirestoreAIProvider {
  id: string;
  name: string;
  type: AIProviderType;
  icon: string;
  color: string;
  enabled: boolean;
  order: number;
  baseUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface FirestoreAIModel {
  id: string;
  providerId: AIProviderType;
  name: string;
  displayName: string;
  description: string;
  contextLength: number;
  maxTokens: number;
  inputPrice: number;
  outputPrice: number;
  capabilities: string[];
  enabled: boolean;
  isDefault: boolean;
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

// Get all AI providers
export async function getAIProviders(): Promise<FirestoreAIProvider[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const providersRef = collection(db, COLLECTIONS.AI_PROVIDERS);
  const q = query(providersRef, orderBy('order', 'asc'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreAIProvider;
  });
}

// Get all AI models
export async function getAIModels(): Promise<FirestoreAIModel[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const modelsRef = collection(db, COLLECTIONS.AI_MODELS);
  const q = query(modelsRef, orderBy('order', 'asc'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreAIModel;
  });
}

// Get models by provider
export async function getAIModelsByProvider(providerId: AIProviderType): Promise<FirestoreAIModel[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const modelsRef = collection(db, COLLECTIONS.AI_MODELS);
  const q = query(
    modelsRef,
    where('providerId', '==', providerId),
    where('enabled', '==', true),
    orderBy('order', 'asc')
  );
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreAIModel;
  });
}

// Add AI provider
export async function addAIProvider(provider: Omit<FirestoreAIProvider, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  if (!isFirebaseConfigured()) throw new Error('Firebase not configured');

  const db = getFirebaseFirestore();
  const providersRef = collection(db, COLLECTIONS.AI_PROVIDERS);
  const newRef = doc(providersRef);

  await setDoc(newRef, {
    ...provider,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return newRef.id;
}

// Add AI model
export async function addAIModel(model: Omit<FirestoreAIModel, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  if (!isFirebaseConfigured()) throw new Error('Firebase not configured');

  const db = getFirebaseFirestore();
  const modelsRef = collection(db, COLLECTIONS.AI_MODELS);
  const newRef = doc(modelsRef);

  await setDoc(newRef, {
    ...model,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return newRef.id;
}

// Update AI provider
export async function updateAIProvider(providerId: string, updates: Partial<FirestoreAIProvider>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const providerRef = doc(db, COLLECTIONS.AI_PROVIDERS, providerId);
  await updateDoc(providerRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// Update AI model
export async function updateAIModel(modelId: string, updates: Partial<FirestoreAIModel>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const modelRef = doc(db, COLLECTIONS.AI_MODELS, modelId);
  await updateDoc(modelRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// Delete AI provider
export async function deleteAIProvider(providerId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const providerRef = doc(db, COLLECTIONS.AI_PROVIDERS, providerId);
  await deleteDoc(providerRef);
}

// Delete AI model
export async function deleteAIModel(modelId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const modelRef = doc(db, COLLECTIONS.AI_MODELS, modelId);
  await deleteDoc(modelRef);
}

// Toggle AI provider enabled status
export async function toggleAIProvider(providerId: string, enabled: boolean): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const providerRef = doc(db, COLLECTIONS.AI_PROVIDERS, providerId);
  await updateDoc(providerRef, {
    enabled,
    updatedAt: serverTimestamp(),
  });
}

// Toggle AI model enabled status
export async function toggleAIModel(modelId: string, enabled: boolean): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const modelRef = doc(db, COLLECTIONS.AI_MODELS, modelId);
  await updateDoc(modelRef, {
    enabled,
    updatedAt: serverTimestamp(),
  });
}

// Set default model for provider
export async function setDefaultAIModel(providerId: AIProviderType, modelId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const modelsRef = collection(db, COLLECTIONS.AI_MODELS);

  // First, unset all defaults for this provider
  const q = query(modelsRef, where('providerId', '==', providerId));
  const snap = await getDocs(q);

  const batch = writeBatch(db);
  snap.docs.forEach((doc) => {
    batch.update(doc.ref, { isDefault: doc.id === modelId, updatedAt: serverTimestamp() });
  });

  await batch.commit();
}

// Listen to AI providers changes
export function onAIProvidersChange(callback: (providers: FirestoreAIProvider[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const providersRef = collection(db, COLLECTIONS.AI_PROVIDERS);
  const q = query(providersRef, orderBy('order', 'asc'));

  return onSnapshot(
    q,
    (snap) => {
      const providers = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
        } as FirestoreAIProvider;
      });
      callback(providers);
    },
    (error) => {
      // Silently handle connection errors - data will be refetched on reconnect
      console.debug('AI Providers listener error (will auto-reconnect):', error.code);
    }
  );
}

// Listen to AI models changes
export function onAIModelsChange(callback: (models: FirestoreAIModel[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const modelsRef = collection(db, COLLECTIONS.AI_MODELS);
  const q = query(modelsRef, orderBy('order', 'asc'));

  return onSnapshot(
    q,
    (snap) => {
      const models = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
        } as FirestoreAIModel;
      });
      callback(models);
    },
    (error) => {
      // Silently handle connection errors - data will be refetched on reconnect
      console.debug('AI Models listener error (will auto-reconnect):', error.code);
    }
  );
}

// Seed initial AI providers and models
export async function seedAIProvidersAndModels(): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();

  // Check if already seeded
  const providersSnap = await getDocs(collection(db, COLLECTIONS.AI_PROVIDERS));
  if (!providersSnap.empty) {
    console.log('AI data already seeded');
    return;
  }

  const batch = writeBatch(db);

  // Seed providers
  const providers = [
    { type: 'openai', name: 'OpenAI', icon: 'Bot', color: 'green', enabled: true, order: 1 },
    { type: 'anthropic', name: 'Anthropic', icon: 'Brain', color: 'orange', enabled: true, order: 2 },
    { type: 'google', name: 'Google AI', icon: 'Sparkles', color: 'blue', enabled: true, order: 3 },
    { type: 'xai', name: 'xAI (Grok)', icon: 'Zap', color: 'purple', enabled: true, order: 4 },
    { type: 'mistral', name: 'Mistral AI', icon: 'Wind', color: 'cyan', enabled: true, order: 5 },
    { type: 'ollama', name: 'Ollama (Local)', icon: 'Server', color: 'gray', enabled: true, order: 6 },
  ];

  providers.forEach((provider) => {
    const ref = doc(collection(db, COLLECTIONS.AI_PROVIDERS));
    batch.set(ref, { ...provider, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });

  // Seed models
  const models = [
    // OpenAI
    { providerId: 'openai', name: 'gpt-4-turbo', displayName: 'GPT-4 Turbo', description: 'Most capable GPT-4 model', contextLength: 128000, maxTokens: 4096, inputPrice: 10, outputPrice: 30, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: true, order: 1 },
    { providerId: 'openai', name: 'gpt-4o', displayName: 'GPT-4o', description: 'Latest multimodal model', contextLength: 128000, maxTokens: 4096, inputPrice: 5, outputPrice: 15, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 2 },
    { providerId: 'openai', name: 'gpt-4o-mini', displayName: 'GPT-4o Mini', description: 'Fast and affordable', contextLength: 128000, maxTokens: 16384, inputPrice: 0.15, outputPrice: 0.6, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 3 },
    { providerId: 'openai', name: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo', description: 'Fast and cost-effective', contextLength: 16385, maxTokens: 4096, inputPrice: 0.5, outputPrice: 1.5, capabilities: ['chat', 'code'], enabled: true, isDefault: false, order: 4 },
    // Anthropic
    { providerId: 'anthropic', name: 'claude-sonnet-4-20250514', displayName: 'Claude Sonnet 4', description: 'Latest Claude model', contextLength: 200000, maxTokens: 8192, inputPrice: 3, outputPrice: 15, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: true, order: 1 },
    { providerId: 'anthropic', name: 'claude-3-5-sonnet-20241022', displayName: 'Claude 3.5 Sonnet', description: 'Fast and intelligent', contextLength: 200000, maxTokens: 8192, inputPrice: 3, outputPrice: 15, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 2 },
    { providerId: 'anthropic', name: 'claude-3-opus-20240229', displayName: 'Claude 3 Opus', description: 'Most capable for complex tasks', contextLength: 200000, maxTokens: 4096, inputPrice: 15, outputPrice: 75, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 3 },
    { providerId: 'anthropic', name: 'claude-3-haiku-20240307', displayName: 'Claude 3 Haiku', description: 'Fastest and most compact', contextLength: 200000, maxTokens: 4096, inputPrice: 0.25, outputPrice: 1.25, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 4 },
    // Google
    { providerId: 'google', name: 'gemini-1.5-pro', displayName: 'Gemini 1.5 Pro', description: 'Advanced reasoning', contextLength: 1000000, maxTokens: 8192, inputPrice: 3.5, outputPrice: 10.5, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: true, order: 1 },
    { providerId: 'google', name: 'gemini-1.5-flash', displayName: 'Gemini 1.5 Flash', description: 'Fast multimodal model', contextLength: 1000000, maxTokens: 8192, inputPrice: 0.075, outputPrice: 0.3, capabilities: ['chat', 'code', 'vision'], enabled: true, isDefault: false, order: 2 },
    // xAI
    { providerId: 'xai', name: 'grok-2', displayName: 'Grok 2', description: 'xAI flagship model', contextLength: 131072, maxTokens: 4096, inputPrice: 2, outputPrice: 10, capabilities: ['chat', 'code'], enabled: true, isDefault: true, order: 1 },
    // Mistral
    { providerId: 'mistral', name: 'mistral-large-latest', displayName: 'Mistral Large', description: 'Flagship model', contextLength: 128000, maxTokens: 8192, inputPrice: 3, outputPrice: 9, capabilities: ['chat', 'code'], enabled: true, isDefault: true, order: 1 },
    { providerId: 'mistral', name: 'codestral-latest', displayName: 'Codestral', description: 'Specialized for code', contextLength: 32000, maxTokens: 8192, inputPrice: 1, outputPrice: 3, capabilities: ['code'], enabled: true, isDefault: false, order: 2 },
    // Ollama
    { providerId: 'ollama', name: 'llama3.2', displayName: 'Llama 3.2', description: 'Latest Llama model', contextLength: 128000, maxTokens: 4096, inputPrice: 0, outputPrice: 0, capabilities: ['chat', 'code'], enabled: true, isDefault: true, order: 1 },
    { providerId: 'ollama', name: 'codellama', displayName: 'Code Llama', description: 'Specialized for code', contextLength: 16384, maxTokens: 4096, inputPrice: 0, outputPrice: 0, capabilities: ['code'], enabled: true, isDefault: false, order: 2 },
    { providerId: 'ollama', name: 'mistral', displayName: 'Mistral 7B', description: 'Efficient open-source', contextLength: 32768, maxTokens: 4096, inputPrice: 0, outputPrice: 0, capabilities: ['chat', 'code'], enabled: true, isDefault: false, order: 3 },
  ];

  models.forEach((model) => {
    const ref = doc(collection(db, COLLECTIONS.AI_MODELS));
    batch.set(ref, { ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });

  await batch.commit();
  console.log('AI providers and models seeded successfully');
}

// ============================================================================
// PRICING
// ============================================================================

export interface FirestorePricing {
  id: string;
  modelId: string;
  displayName: string;
  provider: string;
  tier: string;
  contextWindow: number;
  apiInput: number;
  apiOutput: number;
  input: number;
  output: number;
  isReasoning?: boolean;
  description?: string;
  enabled: boolean;
  lastUpdated: Date;
}

export interface FirestorePricingConfig {
  markup: number;
  pricing: FirestorePricing[];
  savedAt: Date;
}

export async function getPricingConfig(): Promise<FirestorePricingConfig | null> {
  if (!isFirebaseConfigured()) return null;

  const db = getFirebaseFirestore();
  const configRef = doc(db, COLLECTIONS.PRICING, 'config');
  const snap = await getDoc(configRef);

  if (!snap.exists()) return null;

  const data = snap.data();
  return {
    markup: data.markup || 1.2,
    pricing: (data.pricing || []).map((p: FirestorePricing) => ({
      ...p,
      lastUpdated: (p.lastUpdated as unknown as Timestamp)?.toDate?.() || new Date(),
    })),
    savedAt: (data.savedAt as Timestamp)?.toDate() || new Date(),
  };
}

export async function savePricingConfig(config: Omit<FirestorePricingConfig, 'savedAt'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const configRef = doc(db, COLLECTIONS.PRICING, 'config');

  await setDoc(configRef, {
    ...config,
    savedAt: serverTimestamp(),
  });
}

export function onPricingConfigChange(callback: (config: FirestorePricingConfig | null) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback(null);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const configRef = doc(db, COLLECTIONS.PRICING, 'config');

  return onSnapshot(
    configRef,
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }

      const data = snap.data();
      callback({
        markup: data.markup || 1.2,
        pricing: (data.pricing || []).map((p: FirestorePricing) => ({
          ...p,
          lastUpdated: (p.lastUpdated as unknown as Timestamp)?.toDate?.() || new Date(),
        })),
        savedAt: (data.savedAt as Timestamp)?.toDate() || new Date(),
      });
    },
    (error) => {
      console.debug('Pricing listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// SETTINGS
// ============================================================================

export interface FirestoreAdminSettings {
  userId: string;
  profile: {
    displayName: string;
    email: string;
    avatar?: string;
  };
  notifications: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    incidentAlerts: boolean;
    weeklyReports: boolean;
  };
  appearance: {
    theme: 'dark' | 'light' | 'system';
    accentColor: string;
  };
  updatedAt: Date;
}

export async function getAdminSettings(userId: string): Promise<FirestoreAdminSettings | null> {
  if (!isFirebaseConfigured()) return null;

  const db = getFirebaseFirestore();
  const settingsRef = doc(db, COLLECTIONS.SETTINGS, userId);
  const snap = await getDoc(settingsRef);

  if (!snap.exists()) return null;

  const data = snap.data();
  return {
    ...data,
    updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
  } as FirestoreAdminSettings;
}

export async function saveAdminSettings(userId: string, settings: Omit<FirestoreAdminSettings, 'userId' | 'updatedAt'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const settingsRef = doc(db, COLLECTIONS.SETTINGS, userId);

  await setDoc(settingsRef, {
    userId,
    ...settings,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function onAdminSettingsChange(userId: string, callback: (settings: FirestoreAdminSettings | null) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback(null);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const settingsRef = doc(db, COLLECTIONS.SETTINGS, userId);

  return onSnapshot(
    settingsRef,
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }

      const data = snap.data();
      callback({
        ...data,
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      } as FirestoreAdminSettings);
    },
    (error) => {
      console.debug('Settings listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// DASHBOARD AGGREGATES
// ============================================================================

export interface FirestoreDashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalExtensions: number;
  pendingExtensions: number;
  totalContent: number;
  publishedContent: number;
  totalDownloads: number;
  todayPageViews: number;
}

export async function getDashboardStats(): Promise<FirestoreDashboardStats> {
  if (!isFirebaseConfigured()) {
    return {
      totalUsers: 0,
      activeUsers: 0,
      totalExtensions: 0,
      pendingExtensions: 0,
      totalContent: 0,
      publishedContent: 0,
      totalDownloads: 0,
      todayPageViews: 0,
    };
  }

  const db = getFirebaseFirestore();

  // Get users stats
  const usersSnap = await getDocs(collection(db, COLLECTIONS.WEBSITE_USERS));
  const users = usersSnap.docs.map(d => d.data());
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;

  // Get extensions stats
  const extSnap = await getDocs(collection(db, COLLECTIONS.EXTENSIONS));
  const extensions = extSnap.docs.map(d => d.data());
  const totalExtensions = extensions.length;
  const pendingExtensions = extensions.filter(e => e.status === 'pending').length;

  // Get content stats
  const contentSnap = await getDocs(collection(db, COLLECTIONS.CONTENT));
  const content = contentSnap.docs.map(d => d.data());
  const totalContent = content.length;
  const publishedContent = content.filter(c => c.status === 'published').length;

  // Get today's stats
  const today = new Date().toISOString().split('T')[0];
  const todayStatsRef = doc(db, COLLECTIONS.DAILY_STATS, today);
  const todaySnap = await getDoc(todayStatsRef);
  const todayStats = todaySnap.exists() ? todaySnap.data() : { pageViews: 0, downloads: 0 };

  // Get total downloads from extensions
  const totalDownloads = extensions.reduce((sum, e) => sum + (e.downloads || 0), 0);

  return {
    totalUsers,
    activeUsers,
    totalExtensions,
    pendingExtensions,
    totalContent,
    publishedContent,
    totalDownloads,
    todayPageViews: todayStats.pageViews || 0,
  };
}

// Recent activity log
export interface FirestoreActivityLog {
  id: string;
  type: 'info' | 'warning' | 'success' | 'error';
  message: string;
  userId?: string;
  timestamp: Date;
}

export async function logActivity(activity: Omit<FirestoreActivityLog, 'id' | 'timestamp'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const activityRef = collection(db, 'activity_logs');
  const newRef = doc(activityRef);

  await setDoc(newRef, {
    ...activity,
    timestamp: serverTimestamp(),
  });
}

export async function getRecentActivity(limitCount: number = 10): Promise<FirestoreActivityLog[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const activityRef = collection(db, 'activity_logs');
  const q = query(activityRef, orderBy('timestamp', 'desc'), limit(limitCount));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(),
    } as FirestoreActivityLog;
  });
}

export function onDailyStatsChange(callback: (stats: FirestoreDailyStats[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const statsRef = collection(db, COLLECTIONS.DAILY_STATS);
  const q = query(statsRef, orderBy('date', 'desc'), limit(90));

  return onSnapshot(
    q,
    (snap) => {
      const stats = snap.docs.map((doc) => doc.data() as FirestoreDailyStats).reverse();
      callback(stats);
    },
    (error) => {
      console.debug('Daily stats listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// REVENUE
// ============================================================================

export interface FirestoreRevenueRecord {
  id: string;
  date: string;
  userId?: string;
  type: 'subscription' | 'api_usage' | 'one_time';
  amount: number;
  currency: string;
  description?: string;
  createdAt: Date;
}

export interface FirestoreMonthlyRevenue {
  month: string; // YYYY-MM format
  totalRevenue: number;
  totalApiCost: number;
  profit: number;
  profitMargin: number;
  subscriptionRevenue: number;
  apiUsageRevenue: number;
  updatedAt: Date;
}

export async function getMonthlyRevenue(months: number = 12): Promise<FirestoreMonthlyRevenue[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const revenueRef = collection(db, COLLECTIONS.REVENUE);
  const q = query(revenueRef, orderBy('month', 'desc'), limit(months));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreMonthlyRevenue;
  }).reverse();
}

export async function getCurrentMonthRevenue(): Promise<FirestoreMonthlyRevenue | null> {
  if (!isFirebaseConfigured()) return null;

  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const db = getFirebaseFirestore();
  const revenueRef = doc(db, COLLECTIONS.REVENUE, currentMonth);
  const snap = await getDoc(revenueRef);

  if (!snap.exists()) return null;

  const data = snap.data();
  return {
    ...data,
    updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
  } as FirestoreMonthlyRevenue;
}

export async function updateMonthlyRevenue(month: string, updates: Partial<FirestoreMonthlyRevenue>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();
  const revenueRef = doc(db, COLLECTIONS.REVENUE, month);

  const snap = await getDoc(revenueRef);
  if (snap.exists()) {
    await updateDoc(revenueRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } else {
    await setDoc(revenueRef, {
      month,
      totalRevenue: 0,
      totalApiCost: 0,
      profit: 0,
      profitMargin: 0,
      subscriptionRevenue: 0,
      apiUsageRevenue: 0,
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }
}

export function onMonthlyRevenueChange(callback: (revenue: FirestoreMonthlyRevenue[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const db = getFirebaseFirestore();
  const revenueRef = collection(db, COLLECTIONS.REVENUE);
  const q = query(revenueRef, orderBy('month', 'desc'), limit(12));

  return onSnapshot(
    q,
    (snap) => {
      const revenue = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
        } as FirestoreMonthlyRevenue;
      }).reverse();
      callback(revenue);
    },
    (error) => {
      console.debug('Revenue listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

export interface FirestoreSubscription {
  id: string;
  userId: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  amount: number;
  currency: string;
  startDate: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export async function getActiveSubscriptions(): Promise<FirestoreSubscription[]> {
  if (!isFirebaseConfigured()) return [];

  const db = getFirebaseFirestore();
  const subsRef = collection(db, COLLECTIONS.SUBSCRIPTIONS);
  const q = query(subsRef, where('status', '==', 'active'));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startDate: (data.startDate as Timestamp)?.toDate() || new Date(),
      endDate: (data.endDate as Timestamp)?.toDate(),
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreSubscription;
  });
}

export async function getSubscriptionStats(): Promise<{
  total: number;
  active: number;
  cancelled: number;
  trial: number;
  monthlyRecurring: number;
  averageRevenuePerUser: number;
}> {
  if (!isFirebaseConfigured()) {
    return { total: 0, active: 0, cancelled: 0, trial: 0, monthlyRecurring: 0, averageRevenuePerUser: 0 };
  }

  const db = getFirebaseFirestore();
  const subsRef = collection(db, COLLECTIONS.SUBSCRIPTIONS);
  const snap = await getDocs(subsRef);

  const subscriptions = snap.docs.map((doc) => doc.data());
  const active = subscriptions.filter(s => s.status === 'active');
  const monthlyRecurring = active.reduce((sum, s) => sum + (s.amount || 0), 0);

  return {
    total: subscriptions.length,
    active: active.length,
    cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
    trial: subscriptions.filter(s => s.status === 'trial').length,
    monthlyRecurring,
    averageRevenuePerUser: active.length > 0 ? monthlyRecurring / active.length : 0,
  };
}

export function onSubscriptionsChange(callback: (stats: {
  total: number;
  active: number;
  cancelled: number;
  trial: number;
  monthlyRecurring: number;
  averageRevenuePerUser: number;
}) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback({ total: 0, active: 0, cancelled: 0, trial: 0, monthlyRecurring: 0, averageRevenuePerUser: 0 });
    return () => {};
  }

  const db = getFirebaseFirestore();
  const subsRef = collection(db, COLLECTIONS.SUBSCRIPTIONS);

  return onSnapshot(
    subsRef,
    (snap) => {
      const subscriptions = snap.docs.map((doc) => doc.data());
      const active = subscriptions.filter(s => s.status === 'active');
      const monthlyRecurring = active.reduce((sum, s) => sum + (s.amount || 0), 0);

      callback({
        total: subscriptions.length,
        active: active.length,
        cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
        trial: subscriptions.filter(s => s.status === 'trial').length,
        monthlyRecurring,
        averageRevenuePerUser: active.length > 0 ? monthlyRecurring / active.length : 0,
      });
    },
    (error) => {
      console.debug('Subscriptions listener error (will auto-reconnect):', error.code);
    }
  );
}

// ============================================================================
// AI USAGE
// ============================================================================

export interface FirestoreAIUsageRecord {
  id: string;
  userId: string;
  modelId: string;
  modelName: string;
  provider: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  timestamp: Date;
}

export interface FirestoreMonthlyAIUsage {
  month: string; // YYYY-MM format
  modelId: string;
  modelName: string;
  provider: string;
  totalRequests: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  updatedAt: Date;
}

export async function getMonthlyAIUsage(month?: string): Promise<FirestoreMonthlyAIUsage[]> {
  if (!isFirebaseConfigured()) return [];

  const targetMonth = month || new Date().toISOString().slice(0, 7);
  const db = getFirebaseFirestore();
  const usageRef = collection(db, COLLECTIONS.AI_USAGE);
  const q = query(usageRef, where('month', '==', targetMonth));
  const snap = await getDocs(q);

  return snap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      month: data.month || '',
      modelId: data.modelId || '',
      modelName: data.modelName || '',
      provider: data.provider || '',
      totalRequests: data.totalRequests || 0,
      totalInputTokens: data.totalInputTokens || 0,
      totalOutputTokens: data.totalOutputTokens || 0,
      totalCost: data.totalCost || 0,
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as FirestoreMonthlyAIUsage;
  }).sort((a, b) => b.totalCost - a.totalCost); // Sort by cost descending
}

export async function getAIUsageSummary(): Promise<{
  totalCost: number;
  totalRequests: number;
  byModel: { modelName: string; provider: string; cost: number; percentage: number }[];
}> {
  if (!isFirebaseConfigured()) {
    return { totalCost: 0, totalRequests: 0, byModel: [] };
  }

  const currentMonth = new Date().toISOString().slice(0, 7);
  const usage = await getMonthlyAIUsage(currentMonth);

  const totalCost = usage.reduce((sum, u) => sum + u.totalCost, 0);
  const totalRequests = usage.reduce((sum, u) => sum + u.totalRequests, 0);

  const byModel = usage.map(u => ({
    modelName: u.modelName,
    provider: u.provider,
    cost: u.totalCost,
    percentage: totalCost > 0 ? Math.round((u.totalCost / totalCost) * 100) : 0,
  }));

  return { totalCost, totalRequests, byModel };
}

export async function recordAIUsage(usage: Omit<FirestoreAIUsageRecord, 'id' | 'timestamp'>): Promise<void> {
  if (!isFirebaseConfigured()) return;

  const db = getFirebaseFirestore();

  // Record individual usage
  const usageRecordsRef = collection(db, 'ai_usage_records');
  await setDoc(doc(usageRecordsRef), {
    ...usage,
    timestamp: serverTimestamp(),
  });

  // Update monthly aggregation
  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthlyDocId = `${currentMonth}_${usage.modelId}`;
  const monthlyRef = doc(db, COLLECTIONS.AI_USAGE, monthlyDocId);

  const snap = await getDoc(monthlyRef);
  if (snap.exists()) {
    await updateDoc(monthlyRef, {
      totalRequests: increment(1),
      totalInputTokens: increment(usage.inputTokens),
      totalOutputTokens: increment(usage.outputTokens),
      totalCost: increment(usage.cost),
      updatedAt: serverTimestamp(),
    });
  } else {
    await setDoc(monthlyRef, {
      month: currentMonth,
      modelId: usage.modelId,
      modelName: usage.modelName,
      provider: usage.provider,
      totalRequests: 1,
      totalInputTokens: usage.inputTokens,
      totalOutputTokens: usage.outputTokens,
      totalCost: usage.cost,
      updatedAt: serverTimestamp(),
    });
  }
}

export function onAIUsageChange(callback: (usage: FirestoreMonthlyAIUsage[]) => void): () => void {
  if (!isFirebaseConfigured()) {
    callback([]);
    return () => {};
  }

  const currentMonth = new Date().toISOString().slice(0, 7);
  const db = getFirebaseFirestore();
  const usageRef = collection(db, COLLECTIONS.AI_USAGE);
  const q = query(usageRef, where('month', '==', currentMonth));

  return onSnapshot(
    q,
    (snap) => {
      const usage = snap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          month: data.month || '',
          modelId: data.modelId || '',
          modelName: data.modelName || '',
          provider: data.provider || '',
          totalRequests: data.totalRequests || 0,
          totalInputTokens: data.totalInputTokens || 0,
          totalOutputTokens: data.totalOutputTokens || 0,
          totalCost: data.totalCost || 0,
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
        } as FirestoreMonthlyAIUsage;
      }).sort((a, b) => b.totalCost - a.totalCost);
      callback(usage);
    },
    (error) => {
      console.debug('AI Usage listener error (will auto-reconnect):', error.code);
    }
  );
}
